import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import apiClient from "../../business/network/ApiClient";
import { CurrentUser } from "../../business/helper/CurrentUser";

/**
 * OnboardingContext — parallel to MilaContext, never modifies it (design §5.2).
 * The backend is the single writer of onboarding Status; this context only mirrors it
 * (refresh / explicit REST transitions / server-pushed status on streamed responses).
 */
const OnboardingContext = createContext(null);

export const ONBOARDING_STATUS = {
  NotStarted: "NotStarted",
  WelcomeShown: "WelcomeShown",
  InProgress: "InProgress",
  Declined: "Declined",
  Skipped: "Skipped",
  Abandoned: "Abandoned",
  Completed: "Completed",
};

export const OnboardingProvider = ({ children }) => {
  const [status, setStatus] = useState(null); // null = not resolved yet
  const [threadId, setThreadId] = useState(null);
  const [currentStepKey, setCurrentStepKey] = useState(null);
  const [profileId, setProfileId] = useState(null);
  // "__start__" / "__resume__" — consumed once by MilaAiPage to kick off the hub conversation
  const autoPromptRef = useRef(null);
  const [autoPromptVersion, setAutoPromptVersion] = useState(0);

  const getUser = () => CurrentUser.GetCurrentUserData();

  // Same source as MilaContext.language — the tag the `dialect` setting is deduced from.
  const getLanguage = () =>
    navigator.language || navigator.userLanguage || "en-GB";

  const refresh = useCallback(async () => {
    const user = getUser();
    if (!user?.userId) return null;
    const data = await apiClient.getOnboardingStatus(user.userId, user.ouId, getLanguage());
    if (data) {
      setStatus(data.status);
      setThreadId(data.threadId || null);
      setCurrentStepKey(data.currentStepKey || null);
    }
    return data;
  }, []);

  const startOnboarding = useCallback(async () => {
    const user = getUser();
    if (!user?.userId) return null;
    const data = await apiClient.startOnboarding(user.userId, Number(user.ouId), getLanguage());
    if (data?.threadId) {
      setStatus(ONBOARDING_STATUS.InProgress);
      setThreadId(data.threadId);
      setProfileId(data.profileId);
      autoPromptRef.current = "__start__";
      setAutoPromptVersion((v) => v + 1);
    }
    return data;
  }, []);

  const resumeOnboarding = useCallback(async () => {
    const user = getUser();
    if (!user?.userId) return;
    const data = await apiClient.resumeOnboarding(user.userId);
    setStatus(ONBOARDING_STATUS.InProgress);
    if (data?.currentStepKey) setCurrentStepKey(data.currentStepKey);
    autoPromptRef.current = "__resume__";
    setAutoPromptVersion((v) => v + 1);
  }, []);

  const skipOnboarding = useCallback(async () => {
    const user = getUser();
    if (user?.userId) await apiClient.skipOnboarding(user.userId);
    setStatus(ONBOARDING_STATUS.Skipped);
  }, []);

  /** Applied when a streamed OnboardingResponse carries an updated Status/CurrentStepKey. */
  const applyServerUpdate = useCallback((update) => {
    if (update?.Status) setStatus(update.Status);
    if (update?.CurrentStepKey) setCurrentStepKey(update.CurrentStepKey);
  }, []);

  const consumeAutoPrompt = useCallback(() => {
    const p = autoPromptRef.current;
    autoPromptRef.current = null;
    return p;
  }, []);

  const value = useMemo(() => {
    const isConversationActive =
      status === ONBOARDING_STATUS.InProgress || status === ONBOARDING_STATUS.Declined;

    return {
      status,
      onboardingThreadId: threadId,
      currentStepKey,
      profileId,
      autoPromptVersion,
      // Topic Guard routing predicate: while true for a thread, every message on it goes to
      // OnboardingHubV1 and can never reach the general Mila AI pipeline (design §13).
      isActiveThread: (tid) => isConversationActive && !!tid && tid === threadId,
      isConversationActive,
      refresh,
      startOnboarding,
      resumeOnboarding,
      skipOnboarding,
      applyServerUpdate,
      consumeAutoPrompt,
    };
  }, [status, threadId, currentStepKey, profileId, autoPromptVersion,
      refresh, startOnboarding, resumeOnboarding, skipOnboarding,
      applyServerUpdate, consumeAutoPrompt]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  // Graceful no-op fallback so components render safely outside the provider (e.g. tests)
  if (!ctx) {
    return {
      status: null,
      onboardingThreadId: null,
      currentStepKey: null,
      profileId: null,
      autoPromptVersion: 0,
      isActiveThread: () => false,
      isConversationActive: false,
      refresh: async () => null,
      startOnboarding: async () => null,
      resumeOnboarding: async () => {},
      skipOnboarding: async () => {},
      applyServerUpdate: () => {},
      consumeAutoPrompt: () => null,
    };
  }
  return ctx;
};

export default OnboardingContext;
